import {
  BATTLE_PROTOCOL_VERSION,
  BATTLE_SESSION_ID_MAX_LENGTH,
  encodeBattleClientMessage,
  parseBattleServerMessage,
  type BattleServerMessage,
  type BattleSessionSnapshotMessage,
} from "./battleProtocol";

const DEFAULT_RECONNECT_DELAY_MS = 750;
const MAX_RECONNECT_DELAY_MS = 5_000;

export interface NetworkBattleSpectatorClientOptions {
  /** Defaults to the current page's /ws endpoint. */
  url?: string;
  /** The only identity a read-only Battle overlay is allowed to carry. */
  sessionId: string;
  autoReconnect?: boolean;
}

/**
 * Read-only Battle transport for overlays and broadcast views.
 *
 * This class intentionally exposes no participant binding, reconnect token,
 * storage, readiness, input, or mutation API.  It sends exactly one
 * `battle-overlay-hello` per socket and accepts only detached snapshots.
 */
export class NetworkBattleSpectatorClient {
  public readonly sessionId: string;
  public onStateChange: (() => void) | null = null;
  public onError: ((error: { code: string; message: string; fatal: boolean }) => void) | null = null;
  public snapshot: BattleSessionSnapshotMessage | null = null;
  public connected = false;
  public status = "OFFLINE";
  public serverTick = 0;

  private readonly url: string | null;
  private readonly autoReconnect: boolean;
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private manuallyClosed = false;
  private terminalError = false;

  public constructor(options: NetworkBattleSpectatorClientOptions | string) {
    const config = typeof options === "string" ? { sessionId: options } : options;
    const sessionId = config.sessionId.trim();
    if (sessionId.length === 0 || sessionId.length > BATTLE_SESSION_ID_MAX_LENGTH) throw new Error("A battle session id is required");
    this.sessionId = sessionId;
    this.url = config.url ?? null;
    this.autoReconnect = config.autoReconnect ?? true;
  }

  public connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    this.manuallyClosed = false;
    this.terminalError = false;
    this.status = "CONNECTING";
    const url = this.url ?? (() => {
      const scheme = location.protocol === "https:" ? "wss:" : "ws:";
      return `${scheme}//${location.host}/ws`;
    })();
    const socket = new WebSocket(url);
    this.socket = socket;
    socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.sendHello();
    });
    socket.addEventListener("message", event => {
      if (this.socket === socket) this.receive(String(event.data));
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.connected = false;
      if (!this.manuallyClosed && this.status !== "SESSION_NOT_FOUND") {
        this.status = "RECONNECTING";
        if (this.autoReconnect) this.scheduleReconnect();
      } else if (this.manuallyClosed && !this.terminalError) {
        this.status = "OFFLINE";
      }
      this.onStateChange?.();
    });
    socket.addEventListener("error", () => { /* close owns retry and status */ });
  }

  public close(): void {
    this.manuallyClosed = true;
    this.terminalError = false;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = this.socket;
    this.socket = null;
    this.connected = false;
    this.status = "OFFLINE";
    this.snapshot = null;
    this.serverTick = 0;
    socket?.close(1000, "client closed");
    this.onStateChange?.();
  }

  private sendHello(): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    try {
      this.socket.send(encodeBattleClientMessage({
        type: "battle-overlay-hello",
        protocol: BATTLE_PROTOCOL_VERSION,
        sessionId: this.sessionId,
      }));
    } catch {
      // The close event owns reconnect handling.
    }
  }

  private receive(raw: string): void {
    const message = parseBattleServerMessage(raw);
    if (!message) return;
    if (message.type === "battle-error") {
      this.status = message.code;
      this.onError?.(message);
      if (message.fatal) {
        this.manuallyClosed = true;
        this.terminalError = true;
        this.connected = false;
        if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        const socket = this.socket;
        this.socket = null;
        socket?.close(4000, "battle overlay rejected");
      }
      this.onStateChange?.();
      return;
    }
    if (message.type === "battle-overlay-welcome") {
      if (message.protocol !== BATTLE_PROTOCOL_VERSION) {
        const error = { code: "PROTOCOL_MISMATCH", message: `Expected battle protocol ${BATTLE_PROTOCOL_VERSION}`, fatal: true } as const;
        this.status = error.code;
        this.manuallyClosed = true;
        this.terminalError = true;
        this.connected = false;
        this.onError?.(error);
        const socket = this.socket;
        this.socket = null;
        socket?.close(4000, "battle overlay rejected");
        this.onStateChange?.();
        return;
      }
      if (message.sessionId !== this.sessionId || message.snapshot.sessionId !== this.sessionId) return;
      this.connected = true;
      this.reconnectAttempts = 0;
      this.status = message.snapshot.phase.toUpperCase();
      this.serverTick = message.serverTick;
      this.snapshot = message.snapshot;
      this.onStateChange?.();
      return;
    }
    if (message.type === "battle-overlay-snapshot") {
      if (message.sessionId !== this.sessionId || message.snapshot.sessionId !== this.sessionId) return;
      this.connected = true;
      this.status = message.snapshot.phase.toUpperCase();
      this.serverTick = message.tick;
      this.snapshot = message.snapshot;
      this.onStateChange?.();
      return;
    }
    // A spectator never consumes player welcome, event, ack, or left frames.
    // The server does not send them, but keeping this fence client-side makes
    // the read-only boundary explicit if a proxy or future server regresses.
    if ((message as BattleServerMessage).type.startsWith("battle-")) return;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.manuallyClosed) return;
    const delay = Math.min(MAX_RECONNECT_DELAY_MS, DEFAULT_RECONNECT_DELAY_MS * 2 ** this.reconnectAttempts++);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.manuallyClosed) this.connect();
    }, delay);
  }
}
